import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import api from "../services/api";

/**
 * A page of a list, fetched from the server.
 *
 * Every list in this panel used to fetch everything and slice it in the
 * browser. That is fine for eight client systems and wrong for discussions:
 * the register is expected to reach millions of rows, and at that size the
 * browser is not the problem -- the query behind it is. So the page, the
 * filters and the total all come from the server, and the browser holds one
 * page at a time.
 *
 * `params` is compared by value, not identity, so callers can pass an object
 * literal without re-fetching on every render.
 */
export function usePagedList(path, params = {}, perPage = 10) {
  const [page, setPage] = useState(1);
  const [state, setState] = useState({ rows: [], count: 0, loading: true, error: "" });
  const [tick, setTick] = useState(0);

  const key = JSON.stringify(params);
  const stable = useMemo(() => JSON.parse(key), [key]);

  // A narrower filter can leave you on a page that no longer exists. Reset
  // rather than showing an empty page with a pager that says there are more.
  const firstRun = useRef(true);
  useEffect(() => {
    if (firstRun.current) {
      firstRun.current = false;
      return;
    }
    setPage(1);
  }, [key, perPage]);

  useEffect(() => {
    let live = true;
    setState((s) => ({ ...s, loading: true }));
    api
      .get(path, {
        params: { ...stable, limit: perPage, offset: (page - 1) * perPage },
      })
      .then((res) => {
        if (!live) return;
        setState({
          rows: res.data.results || [],
          count: res.data.count ?? 0,
          loading: false,
          error: "",
        });
      })
      .catch(() => {
        if (!live) return;
        setState({ rows: [], count: 0, loading: false, error: "Roʻyxatni yuklab boʻlmadi." });
      });
    return () => {
      live = false;
    };
  }, [path, stable, perPage, page, tick]);

  const totalPages = Math.max(1, Math.ceil(state.count / perPage));
  const reload = useCallback(() => setTick((t) => t + 1), []);

  return { ...state, page, setPage, totalPages, perPage, reload };
}
